import { Text } from '@wix/design-system';
import {
  Ellipsis,
  FeatureRow as FeatureRowUiComp,
} from '@wix/package-picker-components';
import classNames from 'classnames';
import { TooltipLink } from 'components/TooltipLink';
import { RootStore, withInjectedRootStore } from 'providers';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { PaymentCycle_BIMap } from 'services/BiLogger/BiLoggerTypes';
import { getDomainName } from 'utils';
import { PaymentCycle } from '../../../enums';
import { AdvancedPlansFeature, AdvancedPlansProductData } from '../../../types';
import { featureRowDataHooks as dataHooks } from './FeatureRow.datahooks';
import s from './FeatureRow.scss';

interface FeatureRowProps {
  products: AdvancedPlansProductData[];
  preselected?: boolean[];
  feature: AdvancedPlansFeature;
  size?: 'tiny' | 'small' | 'medium';
  isJapanesePackagePicker?: boolean;
  rootStore: RootStore;
}

const FeatureRowComp: React.FC<FeatureRowProps> = ({
  rootStore,
  products,
  preselected,
  feature,
  size = 'small',
  isJapanesePackagePicker = false,
}) => {
  const { t } = useTranslation();
  const { currentCycle } = rootStore.appStore;
  const domainName = getDomainName(rootStore.appStore.experiments);
  const { biLogger } = rootStore.appStore.appContext;

  const onTooltipViewed = () =>
    biLogger.logViewProductFeatureTooltip({
      cycle: PaymentCycle_BIMap[currentCycle],
      fromStep: 'select_plan',
      productId: 0,
      featureKey: feature.translationKey,
    });

  const descriptionAsSubtitle = isJapanesePackagePicker;

  const isCycleSelectorsInProductsPageEnabled =
    rootStore.appStore.isCycleSelectorsInProductsPageEnabled();
  const isDisabledInMonthly =
    isCycleSelectorsInProductsPageEnabled &&
    feature.disabledInMonthlyCycle &&
    currentCycle === PaymentCycle.MONTHLY;

  const disabledInMonthlyLabel = t(
    'premium-purchase-plan.package_picker_and_cycle_selector.feature_table.monthly_domain_warning',
  );

  if (feature.displayProperties?.hidden) {
    return null;
  }

  const translationTags = {
    b: <b />,
    ul: <ul />,
    li: <li />,
    div: (
      <div data-hook={dataHooks.voucherDisclaimer} className={s.adVouchers} />
    ),
    url: (
      <TooltipLink
        size={size}
        feature={feature}
        onClick={biLogger.logClickLinkInProductFeatureTooltip}
      />
    ),
  };

  return (
    <FeatureRowUiComp
      size={isJapanesePackagePicker ? 'big' : 'small'}
      dataHook={dataHooks.row}
    >
      <FeatureRowUiComp.Cell align='space-between' dataHook={dataHooks.header}>
        <FeatureRowUiComp.Header
          name={
            <Trans
              i18nKey={feature.translationKey}
              values={{ domainName }}
              components={{
                ellipsis: (
                  <Ellipsis
                    maxWidth={160}
                    text={domainName ?? ''}
                    componentProps={{
                      size: 'small',
                      weight: 'bold',
                    }}
                  />
                ),
              }}
            />
          }
          nameWeight={isJapanesePackagePicker ? 'bold' : 'normal'}
          onTooltipViewed={onTooltipViewed}
          description={
            <Text
              size={size}
              data-testid={dataHooks.tooltip}
              className={s.descriptionJapan}
              secondary={isJapanesePackagePicker}
            >
              <Trans
                i18nKey={feature.tooltipTranslationKey}
                components={translationTags}
              />
            </Text>
          }
          badge={
            feature.displayProperties?.hasNewBadge
              ? t('features.new_feature.new_badge')
              : ''
          }
          note={t(feature.benefit ?? '')}
          descriptionAsSubtitle={descriptionAsSubtitle}
          isDescriptionSecondary={isJapanesePackagePicker}
          disabledInMonthlyLabel={
            isDisabledInMonthly ? disabledInMonthlyLabel : ''
          }
        />
      </FeatureRowUiComp.Cell>
      {products.map((p, idx) => (
        <FeatureRowUiComp.Cell isPreSelected={preselected?.[idx]} key={idx}>
          <FeatureRowUiComp.Value
            text={t(feature.value?.get(`${p.id}`) ?? '')}
            isEnabled={feature.includedInProducts.has(`${p.id}`)}
            classNames={{
              check: classNames(s.check, {
                [s.japan]: isJapanesePackagePicker,
              }),
              minus: s.minus,
            }}
            isDisabledInMonthly={isDisabledInMonthly}
          />
        </FeatureRowUiComp.Cell>
      ))}
    </FeatureRowUiComp>
  );
};

export const FeatureRow = withInjectedRootStore(FeatureRowComp);
